// §11.6 / D-059 — the Settings app's capability, the pages beyond Personalization.
//
// It lives beside `capabilities.ts` rather than inside it because Settings is the one built-in
// that touches nearly every service — the keymap, the file-type registry, the installed set, the
// server's admin config, the export worker — and folding all of that into the file that holds
// six other capabilities would bury the small ones.
//
// Every method here is something §11.6 names as a page, and nothing here is reachable from
// `HangarAPI`: an app that could rebind a chord or read another app's grants would undo §9.4.
import type {
  AboutView,
  AccountView,
  BindingResult,
  FileTypeView,
  InstalledAppView,
  KeyBindingView,
  SettingDescriptor,
  SettingsShellAccess,
  SkinChoice,
  StorageReadout,
  WallpaperChoice,
} from '@hangar/shared';
import type { HangarKernel } from '@/kernel';
import { SETTINGS, SETTING_IDS, type SettingId } from '@/kernel/settings';
import {
  canonicalChord,
  chordFromEvent,
  chordLabel,
  isBrowserReserved,
  parseModifierTap,
  type KeyEventLike,
} from '@/kernel/keyboard';
import { userRoot, type Binding } from '@hangar/shared';
import { WALLPAPERS, wallpaperCss } from '@/assets/wallpaper';
import { availableSkins } from '@/skins/registry';
import { createServerAccess, beginSignIn } from '@/kernel/server';
import { COPY } from '@/copy';
import { saveBlob } from './download';

/** §6.4's actions, in the user's words. A keymap row with no label here says its own id. */
const ACTION_LABELS: Record<string, string> = {
  'launcher.open': 'Open the launcher',
  'switcher.windows': 'Switch windows',
  'switcher.overview': 'Show every window',
  'window.close': 'Close the window',
  'window.snapLeft': 'Snap left',
  'window.snapRight': 'Snap right',
  'window.snapUp': 'Snap up / maximize',
  'window.snapDown': 'Snap down / restore',
  'desktop.previous': 'Previous desktop',
  'desktop.next': 'Next desktop',
  'clipboard.history': 'Clipboard history',
  'notifications.center': 'Notification Center',
  'shell.immersive': 'Immersive mode',
  'shell.lock': 'Lock',
  'app.explorer': 'Open the File Explorer',
  'app.settings': 'Open Settings',
};

/** D-042 — what the shell probed once at boot, and what each answer costs the user. */
const CAPABILITY_COPY: Record<string, { label: string; consequence: string }> = {
  dragOut: {
    label: 'Drag files out to your desktop',
    consequence: 'Use Download from the context menu instead',
  },
  nativeOpenPicker: {
    label: 'Your operating system’s Open dialog',
    consequence: 'Import by dragging files in',
  },
  nativeSavePicker: {
    label: 'Your operating system’s Save dialog',
    consequence: 'Files download to your usual folder',
  },
  syncAccessHandle: {
    label: 'Fast file writes',
    consequence: 'Writes take a slower path; nothing is lost',
  },
  fileHandleMove: {
    label: 'Atomic renames',
    consequence: 'A rename is a copy and a delete',
  },
  directoryImport: {
    label: 'Dropping a whole folder in',
    consequence: 'Drop the files inside it instead',
  },
};

export function createSettingsAccess(kernel: HangarKernel, version: string): SettingsShellAccess {
  const isSettingId = (id: string): id is SettingId =>
    (SETTING_IDS as readonly string[]).includes(id);

  const server = createServerAccess();

  const overrides = (): Record<string, string> => kernel.settings.get('keymap.overrides');

  /**
   * The active keymap with the user's overrides applied.
   *
   * Computed from the *setting* rather than read back off the keyboard service, because the
   * service learns about a change on the next render (`Desktop.tsx` pushes it) — and a page that
   * showed the old chord for one frame after a rebind would look like the rebind had failed.
   */
  const effective = (): Array<{ binding: Binding; chord: string; overridden: boolean }> => {
    const set = overrides();
    return kernel.keyboard.declared().map((binding) => {
      const raw = set[binding.id];
      const tap = parseModifierTap(raw ?? binding.chord);
      const chord = tap ? tap.canonical : (canonicalChord(raw ?? binding.chord) ?? binding.chord);
      return { binding, chord, overridden: raw !== undefined };
    });
  };

  return {
    catalog(): SettingDescriptor[] {
      return SETTING_IDS.filter((id) => SETTINGS[id].page !== 'hidden').map((id) => {
        const def = SETTINGS[id];
        return {
          id,
          label: def.label,
          page: def.page,
          type: def.type,
          ...(def.options ? { options: def.options } : {}),
        };
      });
    },

    get(id) {
      return isSettingId(id) ? kernel.settings.get(id) : undefined;
    },

    set(id, value) {
      if (!isSettingId(id)) return;
      // The store is typed per key; the capability surface is not, so this is the one place
      // the two meet. Anything outside the catalog was already rejected above.
      (kernel.settings.set as (key: SettingId, value: unknown) => void)(id, value);
    },

    reset(id) {
      if (isSettingId(id)) kernel.settings.reset(id);
    },

    skins(): SkinChoice[] {
      return availableSkins().map((skin) => ({ id: skin.id, label: skin.label }));
    },

    wallpapers(): WallpaperChoice[] {
      return WALLPAPERS.map((wp) => ({ id: wp.id, label: wp.label, css: wallpaperCss(wp.id) }));
    },

    // §15 — the Sounds page's preview. The click is also the gesture that unlocks audio.
    playSound(id) {
      kernel.sound.noteUserGesture();
      kernel.sound.play(id);
    },

    async storage(): Promise<StorageReadout> {
      const [reading, trash] = await Promise.all([kernel.quota.read(), kernel.trash.usage()]);
      return {
        usageBytes: reading.usageBytes,
        quotaBytes: reading.quotaBytes,
        ratio: reading.ratio,
        over: reading.over,
        trashItems: trash.items,
        trashBytes: trash.bytes,
      };
    },

    subscribe(listener) {
      return kernel.settings.store.subscribe(listener);
    },

    // ── Keyboard (§11.6) ────────────────────────────────────────────────────────────────────

    keyBindings(): KeyBindingView[] {
      return effective().map(({ binding, chord, overridden }) => ({
        id: binding.id,
        label: ACTION_LABELS[binding.id] ?? binding.id,
        chord,
        display: chordLabel(chord, kernel.keyboard.platform),
        // The skin's own chord — what Reset goes back to. The three keymaps do not agree (§6.4),
        // so "default" only means anything relative to the active one.
        fallback: chordLabel(binding.chord, kernel.keyboard.platform),
        overridden,
        reserved: binding.reserved === true,
      }));
    },

    setBinding(action, chord): BindingResult {
      const set = { ...overrides() };
      if (chord === null) {
        delete set[action];
        kernel.settings.set('keymap.overrides', set);
        return 'ok';
      }

      const canonical = canonicalChord(chord);
      if (canonical === null) return 'invalid';
      // §6.4 — a chord the browser takes first would look bound and never fire. Refusing it is
      // the difference between a setting and a lie.
      if (isBrowserReserved(canonical)) return 'browser';
      const taken = effective().find((one) => one.chord === canonical);
      if (taken && taken.binding.id !== action) return 'conflict';

      set[action] = canonical;
      kernel.settings.set('keymap.overrides', set);
      return 'ok';
    },

    chordOf(event) {
      return chordFromEvent(event as KeyEventLike);
    },

    // ── Default apps (§9.2) ─────────────────────────────────────────────────────────────────

    fileTypes(): FileTypeView[] {
      const chosen = kernel.settings.get('filetypes.defaults');
      const names = new Map(kernel.launch.launchable().map((app) => [app.id, app.name]));
      return kernel.filetypes.knownExtensions().map((ext) => {
        const candidates = kernel.filetypes.candidatesFor(`/Users/x/f${ext}`).map((match) => ({
          appId: match.appId,
          name: names.get(match.appId) ?? match.appId,
          role: match.role,
        }));
        return {
          ext,
          candidates,
          current: candidates[0]?.appId ?? '',
          chosen: chosen[ext] !== undefined,
        };
      });
    },

    setDefaultApp(ext, appId) {
      if (appId === null) kernel.filetypes.clearDefault(ext);
      else kernel.filetypes.setDefault(ext, appId);
    },

    // ── Storage (§7.6, §7.7) ────────────────────────────────────────────────────────────────

    async exportEverything() {
      // §7.7 — "Non-negotiable: it exists in both modes". Same call for a guest and a user.
      const result = await kernel.exports.exportEverything();
      saveBlob(result.blob, kernel.exports.suggestedFilename());
    },

    /**
     * §11.6 — "Reset guest session".
     *
     * Everything the browser is holding for this session: the user's whole space (which is where
     * `.Trash` lives, §7.8), the version history each of those files accumulated (§7.5 keeps it
     * under `/System`, so deleting the file alone would leave it orphaned), and the settings.
     * Confirmed through §19.4 first, because it is destructive and there is no undo.
     */
    async resetGuestSession() {
      if (!(await kernel.shellui.confirm(COPY.settings.resetConfirm))) return false;
      const root = userRoot(kernel.vfs.currentUser());
      const forget = async (dir: string): Promise<void> => {
        for (const entry of await kernel.vfs.internal.list(dir)) {
          if (entry.isDir) await forget(entry.path);
          else await kernel.versions.forget(entry.path);
        }
      };
      await forget(root);
      for (const entry of await kernel.vfs.internal.list(root)) {
        await kernel.vfs.internal.remove(entry.path, { recursive: true });
      }
      await kernel.vfs.bootstrap(true);
      kernel.settings.resetAll();
      return true;
    },

    // ── Apps (§9.6) ─────────────────────────────────────────────────────────────────────────

    installedApps(): InstalledAppView[] {
      return kernel.installed.list().map((app) => {
        const granted = new Set(app.permissions);
        return {
          id: app.id,
          name: app.manifest.name,
          version: app.manifest.version,
          origin: app.manifest.origin,
          granted: [...granted],
          requested: (app.manifest.permissions ?? []).filter((asked) => !granted.has(asked)),
          health: app.health,
          installedAt: app.installedAt,
        };
      });
    },

    async uninstallApp(appId) {
      // §11.5's confirm, reached from the other page that can remove an app.
      const app = kernel.installed.get(appId);
      const name = app?.manifest.name ?? appId;
      if (!(await kernel.shellui.confirm(COPY.storeUninstallConfirm(name)))) return false;
      for (const win of kernel.wm.list()) if (win.appId === appId) kernel.wm.close(win.id);
      return kernel.installed.uninstall(appId);
    },

    // ── Account (§8.3) ──────────────────────────────────────────────────────────────────────

    async account(): Promise<AccountView> {
      const identity = kernel.identity();
      const config = await server.config();
      return {
        signedIn: identity.kind === 'user',
        displayName: identity.kind === 'user' ? identity.displayName : null,
        isAdmin: kernel.isAdmin(),
        hasServer: config !== null,
        anonymousAccess: config?.anonymousAccess ?? null,
      };
    },

    signIn() {
      beginSignIn(() => kernel.shellui.showToast(COPY.signInUnavailable));
    },

    async signOut() {
      // §16 `POST /auth/logout` → 204. The reload is the point: every service in the kernel was
      // built around who this was, and re-deriving that in place is a second boot path.
      try {
        await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
      } catch {
        // A logout that could not reach the server still ends the session locally on reload.
      }
      if (typeof window !== 'undefined') window.location.assign('/');
    },

    async setAnonymousAccess(on) {
      // §16, §19.3 — admin only, and the server is the one that decides that. The client never
      // gates on its own `isAdmin`: it is a hint for what to show, not an authorization.
      try {
        const response = await fetch('/api/admin/config', {
          method: 'PUT',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ anonymousAccess: on }),
        });
        if (!response.ok) return null;
        const body: unknown = await response.json();
        const value = (body as { anonymousAccess?: unknown }).anonymousAccess;
        return typeof value === 'boolean' ? value : null;
      } catch {
        return null;
      }
    },

    // ── About (§11.6, D-042) ────────────────────────────────────────────────────────────────

    about(): AboutView {
      const probed: Record<string, boolean> = { ...kernel.capabilities };
      return {
        version,
        skin: kernel.settings.get('skin'),
        capabilities: Object.keys(CAPABILITY_COPY).map((id) => ({
          id,
          label: CAPABILITY_COPY[id]?.label ?? id,
          present: probed[id] === true,
          consequence: CAPABILITY_COPY[id]?.consequence ?? '',
        })),
      };
    },
  };
}
